/**
 * The funnel model shared by all three role dashboards.
 *
 * ── What changed, and why ──
 * The first version rendered nine rows. Two of them were not stages:
 *
 *   AI Managed     — ownership, not a step. E1 settled that a lead is never
 *                    "in the AI stage"; it has an `assistedBy` and a stage,
 *                    independently. It was already flagged `virtual` and
 *                    already excluded from the conversion chain, which is the
 *                    code admitting it does not belong in the spine. It also
 *                    duplicated the "who is talking to customers" card sitting
 *                    directly beside it on the manager dashboard.
 *
 *   Show / No-Show — attendance of an appointment, not a stage a lead sits in.
 *                    Nobody's pipeline contains a "show/no-show" bucket. Worse,
 *                    it drew a bar whose width was show + noShow and whose
 *                    label was two separate numbers, so the one row on the
 *                    chart you could not read as a quantity was the row with
 *                    two quantities in it.
 *
 * Both facts are worth showing. Neither is a funnel step. So they are now
 * annotations on the rows they describe: AI share on Inbound, attendance on
 * Appointment. The spine is seven real stages a lead genuinely moves through,
 * and every bar on the chart is now the same kind of thing — which is the
 * property that made the old one hard to read.
 *
 * ── Colour ──
 * No hex literals. The house rule is that colour comes from custom properties
 * so the five themes and both modes keep working; the previous palette hard-
 * coded nine blues and rendered identically in every theme. The spine is a
 * blend from --info to --accent-primary, and the two closing stages are
 * --success, because "sold" should look different from "still working".
 *
 * ── The contract with the backend is unchanged ──
 * Still reads funnel.stages[].count / .reached, funnel.aiManagedOfInbound,
 * funnel.show and funnel.noShow. Nothing server-side moves for this.
 */

/**
 * ── The colour ramp ──
 * Stages are ORDERED data, so they get a sequential ramp in one hue, not a
 * categorical palette. Nine unrelated colours — the original — is the encoding
 * you use for things that have no order, and it has to be decoded before it
 * can be read. A ramp is read directly: darker means further down.
 *
 * Intensity rises toward the sale, which is also the right call visually. The
 * top band is wide and carries a lot of area, so full saturation there is
 * heavy; the bottom bands are narrow and need the punch to stay visible at
 * all. Faded-at-the-bottom would have been backwards on both counts.
 *
 * Built from ONE token mixed toward the surface, never by blending two tokens
 * together. The first attempt ramped --info → --accent-primary and collapsed
 * to flat blue in the office365 theme, where both resolve to #0078d4. Mixing a
 * single token with the background cannot fail that way, and it tracks the
 * theme automatically: tints go light in a light theme and dark in a dark one.
 */
const ramp = (token, pct) => `color-mix(in srgb, var(${token}) ${pct}%, var(--bg-secondary))`;

/**
 * The seven stages a lead actually moves through, in order.
 * `key` matches the backend's stage key verbatim — do not rename.
 */
export const FUNNEL_STAGE_KEYS = [
  'INBOUND',
  'CONTACTED',
  'APPOINTMENT',
  'TEST_DRIVE',
  'NEGOTIATION',
  'CONFIRMED',
  'FINANCE_DELIVERY',
];
// Display labels follow the Sep 2026 lane rename (APPOINTMENT → Qualified,
// CONFIRMED → Quoted). Keys stay as the backend sends them.

export const FUNNEL_STAGE_META = {
  // Working stages — one hue, deepening downward.
  INBOUND:          { label: 'Inbound',     color: ramp('--accent-primary', 42) },
  CONTACTED:        { label: 'Contacted',   color: ramp('--accent-primary', 57) },
  APPOINTMENT:      { label: 'Qualified',   color: ramp('--accent-primary', 72) },
  TEST_DRIVE:       { label: 'Test drive',  color: ramp('--accent-primary', 86) },
  NEGOTIATION:      { label: 'Negotiation', color: ramp('--accent-primary', 100) },
  // The hue changes exactly once, where the meaning does: won, not working.
  // That is the only categorical distinction on this chart, so it is the only
  // place a second colour earns its place.
  CONFIRMED:        { label: 'Quoted',      color: ramp('--success', 82) },
  FINANCE_DELIVERY: { label: 'Delivered',   color: ramp('--success', 100) },
};

/** Stages a consultant sees folded into one "Working" row. */
export const CONSULTANT_EARLY_STAGES = ['INBOUND', 'CONTACTED'];

/** Dealer-principal roll-up. Each bucket's count is "reached its last stage". */
export const DP_BUCKETS = [
  { key: 'INBOUND',   label: 'Inbound',   from: ['INBOUND'] },
  { key: 'WORKING',   label: 'Working',   from: ['CONTACTED'] },
  { key: 'IN_DEAL',   label: 'In deal',   from: ['APPOINTMENT', 'TEST_DRIVE', 'NEGOTIATION'] },
  { key: 'CONFIRMED', label: 'Quoted',    from: ['CONFIRMED'] },
  { key: 'DELIVERED', label: 'Delivered', from: ['FINANCE_DELIVERY'] },
];

export function pickCategory(split, category) {
  if (!split) return 0;
  if (category === 'NEW') return split.new || 0;
  if (category === 'USED') return split.used || 0;
  return split.total || 0;
}

const share = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : null);

/**
 * Rows for the full funnel.
 *
 * Every row carries:
 *   count    — leads that reached this stage in the window (cumulative, as before)
 *   width    — share of intake, 0..1. ONE baseline for the whole chart, so the
 *              bars are comparable to each other. The old chart divided by the
 *              largest row and then floored every bar at 10%, which drew a
 *              visible bar for zero — the reason an entirely empty funnel still
 *              looked like it contained something.
 *   conv     — conversion from the PREVIOUS stage, or null on the first row.
 *   ofIntake — conversion from Inbound. Both are useful and they are not the
 *              same number; the old chart showed one of them with no label,
 *              which invites reading it as the other.
 *   sub      — the annotation line (AI share, attendance), or null.
 */
export function buildFunnelRows(funnel, category = 'ALL') {
  if (!funnel || !Array.isArray(funnel.stages)) return null;

  const byStage = Object.fromEntries(funnel.stages.map((s) => [s.stage, s]));
  const reachedOf = (key) => pickCategory(byStage[key]?.reached, category);

  const intake = reachedOf('INBOUND');
  const aiManaged = pickCategory(funnel.aiManagedOfInbound, category);
  const show = pickCategory(funnel.show, category);
  const noShow = pickCategory(funnel.noShow, category);

  let previous = null;
  const rows = FUNNEL_STAGE_KEYS.map((key) => {
    const count = reachedOf(key);
    const conv = previous == null ? null : share(count, previous);
    previous = count;

    let sub = null;
    if (key === 'INBOUND' && aiManaged > 0) {
      sub = `${aiManaged} AI-managed${share(aiManaged, intake) != null ? ` · ${share(aiManaged, intake)}%` : ''}`;
    }
    // Attendance belongs to the appointment, so it is said on the appointment
    // row. A no-show rate is the number a manager acts on; two raw counts are
    // not. Shown only when appointments were actually attended or missed —
    // "0 showed · 0 no-show" is noise, not information.
    if (key === 'APPOINTMENT' && show + noShow > 0) {
      sub = `${show} showed · ${noShow} no-show (${share(noShow, show + noShow)}% missed)`;
    }

    return {
      ...FUNNEL_STAGE_META[key],
      key,
      count,
      sub,
      conv,
      ofIntake: key === 'INBOUND' ? null : share(count, intake),
      width: intake > 0 ? count / intake : 0,
    };
  });

  // Deliberately a property of the result rather than something each caller
  // re-derives: three dashboards render this and all three need the same
  // answer to "is there anything here at all".
  rows.isEmpty = rows.every((r) => r.count === 0);
  rows.intake = intake;
  rows.attendance = { show, noShow };
  rows.aiManaged = aiManaged;

  /**
   * The All / New / Used tabs do not add up, and nothing said so.
   *
   * The backend splits every count five ways — total, new, used, demo,
   * unknown — and this module only ever read three of them. So a window with
   * 5 inbound leads (1 new, 2 used, 1 demo, 1 unknown) showed 5 on All, 1 on
   * New and 2 on Used, and the missing 2 were simply gone. A manager comparing
   * the tabs finds numbers that do not reconcile and has no way to learn why.
   *
   * E1 settled that Unclassified is a real bucket, not a gap to hide. This is
   * the count the chart owes the reader; FunnelChart prints it under the table.
   * A Demo tab is the fuller answer, but that is a product decision, not
   * something to slip in here.
   */
  const intakeSplit = byStage.INBOUND?.reached || {};
  rows.demo = intakeSplit.demo || 0;
  rows.unknown = intakeSplit.unknown || 0;
  rows.unclassified = rows.demo + rows.unknown;

  return rows;
}

/**
 * Consultant view — Inbound and Contacted fold into one "Working" row.
 * A consultant does not manage intake; they manage what is in front of them.
 */
export function buildConsultantRows(funnel, category = 'ALL') {
  const rows = buildFunnelRows(funnel, category);
  if (!rows) return null;
  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
  const working = {
    ...FUNNEL_STAGE_META.CONTACTED,
    key: 'WORKING',
    label: 'Working',
    count: byKey.CONTACTED.count,
    sub: 'Inbound + contacted',
    conv: null,
    ofIntake: null,
    width: byKey.CONTACTED.width,
    combined: true,
  };
  const out = [working, ...rows.filter((r) => !CONSULTANT_EARLY_STAGES.includes(r.key))];
  out.isEmpty = rows.isEmpty;
  out.intake = rows.intake;
  return out;
}

export function buildDpBuckets(funnel, category = 'ALL') {
  const rows = buildFunnelRows(funnel, category);
  if (!rows) return null;
  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
  const out = DP_BUCKETS.map((b) => {
    const last = b.from[b.from.length - 1];
    return { key: b.key, label: b.label, count: byKey[last]?.count ?? 0 };
  });
  out.isEmpty = rows.isEmpty;
  return out;
}
